import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, User, Lock } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/format";
import { sound } from "@/lib/sound";

export default function Start() {
  const { user, loading, startAnonymous } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from?.pathname || "/";

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(dest, { replace: true });
  }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    sound.play("tap");
    setBusy(true);
    try {
      await startAnonymous(name);
      sound.play("success");
      navigate(dest, { replace: true });
    } catch (err) {
      sound.play("error");
      toast.error(
        err.response?.status === 429
          ? "Too many new sessions from this network — wait a minute and retry."
          : errMsg(err, "Could not start — try again")
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5 py-8">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[440px] hp-glass-strong rounded-[2rem] px-6 pt-8 pb-7"
      >
        <div className="flex justify-center">
          <Brand size="hero" to={null} float />
        </div>

        <h1 className="mt-4 text-center text-[38px] leading-[1.05] font-extrabold text-white">
          Split the bill
          <br />
          <span className="text-[#a855f7]">in seconds.</span>
        </h1>

        <p className="mt-4 text-center text-[15px] leading-relaxed text-muted-foreground">
          No signup. No passwords.
          <br />
          Just your name and you&apos;re in.
        </p>

        <form onSubmit={start} className="mt-7 space-y-3.5">
          <div className="relative">
            <User
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              data-testid="start-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What's your name?"
              autoFocus
              className="w-full h-[58px] pl-12 pr-4 rounded-2xl bg-white/[0.04] border border-white/10 text-white text-[17px] placeholder:text-muted-foreground outline-none focus:border-[#a855f7]/60 transition-colors"
            />
          </div>
          <button
            data-testid="start-btn"
            type="submit"
            disabled={busy || !name.trim()}
            className="hp-cta w-full h-[58px] rounded-2xl font-bold text-white text-[18px] flex items-center justify-center gap-2"
          >
            {busy ? "Starting…" : "Start"}
            {!busy && <ArrowRight size={20} />}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
          <Lock size={14} />
          Your bills stay on this device.
        </div>
      </motion.div>
    </div>
  );
}
